import React, { useState, useEffect } from "react";
import {
  Modal,
  Card,
  Badge,
  Spinner,
  Alert,
  Row,
  Col,
  ProgressBar,
} from "react-bootstrap";
import {
  IconCreditCard,
  IconCalendar,
  IconUser,
  IconMail,
  IconPhone,
  IconCheck,
  IconClock,
  IconAlertTriangle,
  IconX,
} from "@tabler/icons-react";
import CustomerSubscriptionServices from "../../../services/CustomerSubscriptionServices";

const SubscriptionDetailsModal = ({ show, onHide, subscriptionId }) => {
  const [loading, setLoading] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (show && subscriptionId) {
      loadSubscriptionDetails();
    }
  }, [show, subscriptionId]);

  const loadSubscriptionDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await CustomerSubscriptionServices.getSubscriptionDetails(subscriptionId);
      setSubscriptionDetails(response.data);
    } catch (error) {
      console.error("Erro ao carregar detalhes da assinatura:", error);
      setError("Não foi possível carregar os detalhes da assinatura.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSubscriptionDetails(null);
    setError(null);
    onHide();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(parseFloat(value || 0));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
      return new Date(dateString).toLocaleDateString("pt-BR");
    } catch (error) {
      return "Data inválida";
    }
  };

  const getStatusBadge = (status, statusDescription) => {
    const statusMap = {
      "ACTIVE": { variant: "success", text: statusDescription || "Ativa" },
      "INACTIVE": { variant: "secondary", text: statusDescription || "Inativa" },
      "CANCELED": { variant: "danger", text: statusDescription || "Cancelada" },
      "PENDING": { variant: "warning", text: statusDescription || "Pendente" },
    };

    const statusInfo = statusMap[status] || { variant: "secondary", text: status };
    return <Badge bg={statusInfo.variant}>{statusInfo.text}</Badge>;
  };

  const getBillingTypeBadge = (billingType, description) => {
    const typeMap = {
      "BOLETO": { variant: "primary", icon: <IconCreditCard size={14} /> },
      "CREDIT_CARD": { variant: "info", icon: <IconCreditCard size={14} /> },
      "PIX": { variant: "success", icon: <IconCreditCard size={14} /> },
    };

    const typeInfo = typeMap[billingType] || { variant: "secondary", icon: <IconCreditCard size={14} /> };
    
    return (
      <Badge bg={typeInfo.variant} className="d-flex align-items-center gap-1">
        {typeInfo.icon}
        {description || billingType}
      </Badge>
    );
  };

  const getCycleBadge = (cycle, description) => {
    const cycleMap = {
      "MONTHLY": { variant: "primary", text: description || "Mensal" },
      "QUARTERLY": { variant: "info", text: description || "Trimestral" },
      "SEMIANNUALLY": { variant: "warning", text: description || "Semestral" },
      "YEARLY": { variant: "success", text: description || "Anual" },
    };

    const cycleInfo = cycleMap[cycle] || { variant: "secondary", text: cycle };
    return <Badge bg={cycleInfo.variant}>{cycleInfo.text}</Badge>;
  };

  const renderSubscriptionInfo = () => {
    const { subscription_details } = subscriptionDetails;
    
    return (
      <Card className="mb-3">
        <Card.Header className="d-flex align-items-center gap-2">
          <IconCreditCard size={20} />
          <h6 className="mb-0">Informações da Assinatura</h6>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <label className="form-label fw-bold text-muted">ID da Assinatura</label>
                <div className="text-dark">{subscription_details.id}</div>
              </div>
              <div className="mb-3">
                <label className="form-label fw-bold text-muted">Status</label>
                <div>{getStatusBadge(subscription_details.status, subscription_details.status_description)}</div>
              </div>
              <div className="mb-3">
                <label className="form-label fw-bold text-muted">Valor</label>
                <div className="text-dark h5 mb-0">{formatCurrency(subscription_details.value)}</div>
              </div>
              <div className="mb-3">
                <label className="form-label fw-bold text-muted">Tipo de Cobrança</label>
                <div>{getBillingTypeBadge(subscription_details.billing_type, subscription_details.billing_type_description)}</div>
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <label className="form-label fw-bold text-muted">Ciclo</label>
                <div>{getCycleBadge(subscription_details.cycle, subscription_details.cycle_description)}</div>
              </div>
              <div className="mb-3">
                <label className="form-label fw-bold text-muted">Descrição</label>
                <div className="text-dark">{subscription_details.description}</div>
              </div>
              <div className="mb-3">
                <label className="form-label fw-bold text-muted">Data de Criação</label>
                <div className="text-dark d-flex align-items-center gap-1">
                  <IconCalendar size={16} />
                  {formatDate(subscription_details.date_created)}
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label fw-bold text-muted">Próximo Vencimento</label>
                <div className="text-dark d-flex align-items-center gap-1">
                  <IconClock size={16} />
                  {formatDate(subscription_details.next_due_date)}
                </div>
              </div>
            </Col>
          </Row>

          {subscription_details.end_date && (
            <div className="mb-3">
              <label className="form-label fw-bold text-muted">Data de Término</label>
              <div className="text-dark d-flex align-items-center gap-1">
                <IconCalendar size={16} />
                {formatDate(subscription_details.end_date)}
              </div>
            </div>
          )}

          {subscription_details.max_payments && (
            <div className="mb-3">
              <label className="form-label fw-bold text-muted">Máximo de Pagamentos</label>
              <div className="text-dark">{subscription_details.max_payments}</div>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  const renderPaymentInfo = () => {
    const { payment_info } = subscriptionDetails;
    if (!payment_info) return null;

    const progressPercentage = payment_info.total_payments_made && subscriptionDetails.subscription_details.max_payments
      ? (payment_info.total_payments_made / subscriptionDetails.subscription_details.max_payments) * 100
      : 0;

    return (
      <Card className="mb-3">
        <Card.Header className="d-flex align-items-center gap-2">
          <IconCheck size={20} />
          <h6 className="mb-0">Informações de Pagamento</h6>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={4}>
              <div className="text-center p-3 bg-light rounded">
                <div className="h3 text-primary mb-0">{payment_info.total_payments_made || 0}</div>
                <small className="text-muted">Pagamentos Realizados</small>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center p-3 bg-light rounded">
                <div className="h3 text-warning mb-0">{payment_info.remaining_payments || 0}</div>
                <small className="text-muted">Pagamentos Restantes</small>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center p-3 bg-light rounded">
                <div className="h3 text-success mb-0">{formatCurrency(payment_info.next_payment?.value)}</div>
                <small className="text-muted">Próximo Pagamento</small>
              </div>
            </Col>
          </Row>

          {progressPercentage > 0 && (
            <div className="mt-3">
              <label className="form-label fw-bold text-muted">Progresso dos Pagamentos</label>
              <ProgressBar 
                now={progressPercentage} 
                label={`${Math.round(progressPercentage)}%`}
                variant={progressPercentage === 100 ? "success" : "primary"}
              />
            </div>
          )}

          {payment_info.next_payment && (
            <div className="mt-3">
              <h6 className="text-muted">Próximo Pagamento</h6>
              <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                <div>
                  <div className="fw-bold">{payment_info.next_payment.description}</div>
                  <small className="text-muted">Vencimento: {formatDate(payment_info.next_payment.due_date)}</small>
                </div>
                <div className="text-success fw-bold h6 mb-0">
                  {formatCurrency(payment_info.next_payment.value)}
                </div>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  const renderCustomerInfo = () => {
    const { customer_info } = subscriptionDetails;
    if (!customer_info) return null;

    return (
      <Card className="mb-3">
        <Card.Header className="d-flex align-items-center gap-2">
          <IconUser size={20} />
          <h6 className="mb-0">Informações do Cliente</h6>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <label className="form-label fw-bold text-muted">Nome</label>
                <div className="text-dark d-flex align-items-center gap-2">
                  <IconUser size={16} />
                  {customer_info.name}
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label fw-bold text-muted">E-mail</label>
                <div className="text-dark d-flex align-items-center gap-2">
                  <IconMail size={16} />
                  {customer_info.email}
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <label className="form-label fw-bold text-muted">Telefone</label>
                <div className="text-dark d-flex align-items-center gap-2">
                  <IconPhone size={16} />
                  {customer_info.phone}
                </div>
              </div>
              {customer_info.card_last_digits && (
                <div className="mb-3">
                  <label className="form-label fw-bold text-muted">Cartão (últimos dígitos)</label>
                  <div className="text-dark d-flex align-items-center gap-2">
                    <IconCreditCard size={16} />
                    **** **** **** {customer_info.card_last_digits}
                  </div>
                </div>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>
    );
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <IconCreditCard size={24} />
          Detalhes da Assinatura
          {subscriptionDetails && (
            <Badge bg="secondary" className="ms-2">
              #{subscriptionDetails.subscription_id}
            </Badge>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <div className="text-center">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Carregando detalhes da assinatura...</p>
            </div>
          </div>
        ) : error ? (
          <Alert variant="danger" className="d-flex align-items-center gap-2">
            <IconAlertTriangle size={20} />
            {error}
          </Alert>
        ) : subscriptionDetails ? (
          <div>
            {subscriptionDetails.success === false ? (
              <Alert variant="warning" className="d-flex align-items-center gap-2">
                <IconAlertTriangle size={20} />
                Não foi possível carregar os detalhes da assinatura.
              </Alert>
            ) : (
              <>
                {renderSubscriptionInfo()}
                {renderPaymentInfo()}
                {renderCustomerInfo()}
              </>
            )}
          </div>
        ) : null}
      </Modal.Body>
    </Modal>
  );
};

export default SubscriptionDetailsModal;